import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, EmptyState } from '../../../components';
import { formatDateTime } from '../../../lib/format';
import { useSignOut } from '../../auth/useAuth';
import { SectionCard } from '../SectionCard';
import { useRevokeSession, useSessions } from '../useAccount';
import type { AccountSession } from '../api';

function deviceLabel(session: AccountSession): string {
  const parts = [session.browser, session.platform].filter((part): part is string => part !== null);
  if (parts.length > 0) return parts.join(' on ');
  if (session.deviceType !== null) return session.deviceType;
  return 'Unknown device';
}

function locationLabel(session: AccountSession): string | null {
  const parts = [session.ipCity, session.ipCountry].filter((part): part is string => part !== null);
  if (parts.length > 0) return parts.join(', ');
  return session.ipAddress;
}

function SessionRow({
  session,
  onRevoked,
}: {
  session: AccountSession;
  onRevoked: (wasCurrentSession: boolean) => void;
}) {
  const revoke = useRevokeSession();
  const location = locationLabel(session);

  return (
    <li className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 fs-13 text-ink">
          <span className="truncate font-medium">{deviceLabel(session)}</span>
          {session.isCurrent && (
            <span className="flex-none rounded-full bg-accent-tint px-2 py-0.5 fs-10 font-bold text-accent">
              This device
            </span>
          )}
        </div>
        <div className="mt-0.5 fs-11 text-muted">
          {location !== null ? `${location} · ` : ''}
          Last active {formatDateTime(new Date(session.lastActivityAt).toISOString())}
        </div>
      </div>
      <Button
        variant="danger"
        size="sm"
        disabled={revoke.isPending}
        onClick={() =>
          revoke.mutate(session.sessionId, { onSuccess: (result) => onRevoked(result.wasCurrentSession) })
        }
      >
        {revoke.isPending ? 'Revoking…' : session.isCurrent ? 'Sign out' : 'Revoke'}
      </Button>
    </li>
  );
}

/**
 * `GET /auth/sessions` / `DELETE /auth/sessions/:sessionId`, group `device`.
 *
 * Revoking a row that turns out to be the caller's own is handled exactly like
 * a password change (see ChangePasswordSection's comment): nauth reports it
 * back as `wasCurrentSession`, and rather than let the list quietly refetch
 * into a state where "am I signed in" is ambiguous, this ends the local
 * session the same deliberate way — an explicit message, an immediate
 * `signOut.mutate()` to clear the dead token and cache, and a delayed redirect
 * with a manual link for anyone who does not want to wait.
 */
export function SessionsSection() {
  const query = useSessions();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [revokedSelf, setRevokedSelf] = useState(false);

  useEffect(() => {
    if (!revokedSelf) return undefined;
    const timer = setTimeout(() => navigate('/signin', { replace: true }), 2500);
    return () => clearTimeout(timer);
  }, [revokedSelf, navigate]);

  const handleRevoked = (wasCurrentSession: boolean) => {
    if (!wasCurrentSession) return;
    setRevokedSelf(true);
    signOut.mutate();
  };

  if (revokedSelf) {
    return (
      <SectionCard title="Sessions">
        <p className="fs-13 text-ink-soft">You signed this device out. Redirecting to sign-in…</p>
        <Link to="/signin" className="mt-2 inline-block fs-13 font-semibold text-accent hover:underline">
          Continue to sign in now →
        </Link>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Sessions"
      description="Every device currently signed in to your account. Revoking one signs it out immediately."
    >
      {query.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : query.error !== null ? (
        <EmptyState title="Could not load sessions" description={query.error.message} />
      ) : query.data.sessions.length === 0 ? (
        <p className="fs-13 text-muted">No active sessions.</p>
      ) : (
        <ul className="-m-4">
          {query.data.sessions.map((session) => (
            <SessionRow key={session.sessionId} session={session} onRevoked={handleRevoked} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
